import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Radio, Gavel, Users, Trophy, Tv, Settings, ClipboardList } from "lucide-react";
import { fmtKES, fmtTime } from "@/lib/format";
import AdminAnalytics from "@/components/AdminAnalytics";
import { roleHome } from "@/lib/roleHome";

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState({ players: 0, teams: 0, matches: 0, liveMatches: [] });
  const [auction, setAuction] = useState(null);
  const [cfg, setCfg] = useState(null);

  // Phase 7: role-based landing — non-admins get their own portal
  useEffect(() => {
    if (!user) return;
    const home = roleHome(user.role);
    if (home !== "/dashboard") nav(home, { replace: true });
  }, [user, nav]);

  useEffect(() => {
    (async () => {
      try {
        const [p, t, m, a, c] = await Promise.all([
          api.get("/players"), api.get("/teams"), api.get("/matches"),
          api.get("/auction"), api.get("/config"),
        ]);
        setStats({
          players: p.data.length,
          teams: t.data.length,
          matches: m.data.length,
          liveMatches: m.data.filter(x => x.status === "live"),
        });
        setAuction(a.data);
        setCfg(c.data);
      } catch (e) { console.error("[Dashboard] Failed to load stats", e); }
    })();
  }, []);

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Welcome back, {user.name}</div>
        <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white mt-1 leading-none">
          Command<br/><span className="text-[#00A86B]">Dashboard</span>
        </h1>
      </div>

      {/* Admin analytics snapshot (super_admin + league_admin only) */}
      <AdminAnalytics role={user.role} />

      {/* Quick metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <StatCard label="Players" value={stats.players} data-testid="stat-players" />
        <StatCard label="Teams" value={stats.teams} data-testid="stat-teams" />
        <StatCard label="Matches" value={stats.matches} data-testid="stat-matches" />
        <StatCard label="Live Now" value={stats.liveMatches.length} accent data-testid="stat-live" />
      </div>

      {/* Live matches */}
      {stats.liveMatches.length > 0 && (
        <Card className="bg-[#11241b] border-white/10 p-6 mb-6" data-testid="dashboard-live-matches">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"/>
            <span className="font-anton uppercase text-xl text-white tracking-tight">Live On Court</span>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {stats.liveMatches.map((m) => (
              <button
                key={m.id}
                data-testid={`live-match-${m.id}`}
                onClick={() => nav(`/spectator/${m.id}`)}
                className="text-left p-5 rounded-xl bg-[#060d09] border border-white/10 hover:border-[#00A86B]/60 transition-all group"
              >
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">{m.court} · Period {m.current_period}</div>
                <div className="mt-2 flex items-baseline justify-between gap-3">
                  <div className="font-anton text-2xl uppercase text-white truncate">{m.team_a?.name || "Team A"}</div>
                  <div className="font-mono-score text-3xl text-white">{m.score_a} <span className="text-zinc-600">·</span> {m.score_b}</div>
                  <div className="font-anton text-2xl uppercase text-white truncate text-right">{m.team_b?.name || "Team B"}</div>
                </div>
                <div className="mt-3 text-xs text-[#00A86B] font-mono-score">{fmtTime(m.match_remaining)} remaining</div>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Quick actions */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {user.role !== "spectator" && (
          <ActionCard icon={Tv} title="Spectator Broadcast" desc="Watch the live scoreboard" onClick={() => nav("/spectator")} data-testid="action-spectator" />
        )}
        {["super_admin", "league_admin", "court_umpire"].includes(user.role) && (
          <ActionCard icon={Radio} title="Match Control" desc="Umpire control panel" onClick={() => nav("/matches")} data-testid="action-matches" />
        )}
        {["super_admin", "league_admin", "owner", "spectator"].includes(user.role) && (
          <ActionCard icon={Gavel} title="Live Auction" desc={auction?.status === "running" ? "AUCTION LIVE NOW" : "Theater of bids"} accent={auction?.status === "running"} onClick={() => nav("/auction")} data-testid="action-auction" />
        )}
        {["super_admin", "league_admin", "owner", "spectator"].includes(user.role) && (
          <ActionCard icon={Users} title="Players" desc="Profiles, photos, ratings" onClick={() => nav("/players")} data-testid="action-players" />
        )}
        {["super_admin", "league_admin", "owner", "spectator"].includes(user.role) && (
          <ActionCard icon={Trophy} title="Teams & Rosters" desc="16 franchises, KES purses" onClick={() => nav("/teams")} data-testid="action-teams" />
        )}
        {["super_admin", "league_admin"].includes(user.role) && (
          <ActionCard icon={ClipboardList} title="Operations" desc="Stalls · Sponsors · Schedule" onClick={() => nav("/operations")} data-testid="action-operations" />
        )}
        {user.role === "super_admin" && (
          <ActionCard icon={Settings} title="Master Config" desc="Every timer, fee & rule" onClick={() => nav("/admin-config")} data-testid="action-config" />
        )}
      </div>

      {cfg && (
        <Card className="mt-8 p-6 bg-[#11241b] border-white/10">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">Tournament At-a-Glance</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><div className="text-zinc-500">League</div><div className="text-white">{cfg.league_start_date} → {cfg.league_end_date}</div></div>
            <div><div className="text-zinc-500">Auction</div><div className="text-white">{cfg.auction_date}</div></div>
            <div><div className="text-zinc-500">Base Purse</div><div className="text-white">{fmtKES(cfg.base_team_purse)}</div></div>
            <div><div className="text-zinc-500">Prize Pool</div><div className="text-[#00A86B] font-semibold">{fmtKES(cfg.prize_pool_total)}</div></div>
          </div>
        </Card>
      )}
    </div>
  );
}

function StatCard({ label, value, accent, ...rest }) {
  return (
    <div {...rest} className={`p-5 rounded-xl border ${accent ? "border-[#00A86B]/40 bg-[#00A86B]/5" : "border-white/10 bg-[#11241b]"}`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-anton text-5xl tracking-tighter mt-1 ${accent ? "text-[#00A86B]" : "text-white"}`}>{value}</div>
    </div>
  );
}

function ActionCard({ icon: Icon, title, desc, onClick, accent, ...rest }) {
  return (
    <button
      {...rest}
      onClick={onClick}
      className={`text-left p-5 rounded-xl border transition-all group ${accent ? "border-[#00A86B] bg-[#00A86B]/10 gold-glow" : "border-white/10 bg-[#11241b] hover:border-white/30"}`}
    >
      <Icon className={`w-6 h-6 mb-3 ${accent ? "text-[#00A86B]" : "text-zinc-400 group-hover:text-white"}`} />
      <div className="font-anton text-xl uppercase tracking-tight text-white">{title}</div>
      <div className="text-xs text-zinc-400 mt-1">{desc}</div>
    </button>
  );
}
